import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Cpu, Hammer, Layers, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { id: "bom",            label: "BOM",            icon: Layers, hint: "Bill of materials — parts, qty, vendors, lead time." },
  { id: "dfm_tracker",    label: "DFM tracker",    icon: Hammer, hint: "Design-for-manufacturability issues + resolution." },
  { id: "iteration_log",  label: "Prototypes",     icon: Cpu,    hint: "Prototype iterations — v1, v2, learnings." },
];

const SCHEMAS = {
  bom: [
    { k: "part_name",      l: "Part name *",     required: true, full: true },
    { k: "quantity",       l: "Qty",             type: "number" },
    { k: "unit_cost",      l: "Unit cost",       type: "number" },
    { k: "currency",       l: "Currency",        select: ["INR", "USD"] },
    { k: "vendor",         l: "Vendor" },
    { k: "lead_time_days", l: "Lead time (d)",   type: "number" },
    { k: "status",         l: "Status",          select: ["sourcing", "quoted", "ordered", "received", "rejected"] },
    { k: "notes",          l: "Notes",           textarea: true, full: true },
  ],
  dfm_tracker: [
    { k: "title",     l: "Issue *",                       required: true, full: true },
    { k: "severity",  l: "Severity",                      select: ["low", "medium", "high", "blocker"] },
    { k: "status",    l: "Status",                        select: ["open", "investigating", "resolved", "wontfix"] },
    { k: "owner",     l: "Owner (name or initials)" },
    { k: "opened_at", l: "Opened",                        type: "date" },
    { k: "closed_at", l: "Closed",                        type: "date" },
    { k: "resolution",l: "Resolution / next step",        textarea: true, full: true },
  ],
  iteration_log: [
    { k: "version",   l: "Version (e.g. P1, v2) *",       required: true },
    { k: "date",      l: "Date",                           type: "date" },
    { k: "summary",   l: "What changed",                   textarea: true, full: true, required: true },
    { k: "learnings", l: "What we learned",                textarea: true, full: true },
    { k: "image_url", l: "Photo URL" },
  ],
};

const STATUS_TONE = {
  sourcing: "blue", quoted: "yellow", ordered: "blue", received: "green", rejected: "red",
  open: "yellow", investigating: "blue", resolved: "green", wontfix: "slate",
  low: "slate", medium: "yellow", high: "orange", blocker: "red",
};

const formatVal = (k, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (k === "unit_cost" && typeof v === "number") return v.toLocaleString("en-IN");
  return String(v);
};

export default function HardwareTabs({ projectId, canEdit }) {
  const [tab, setTab] = useState("bom");
  const [items, setItems] = useState({});
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null); // {kind, item}
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await Promise.all(KINDS.map(async (k) => {
        const { data } = await api.get(`/projects/${projectId}/hw/${k.id}`);
        return [k.id, data];
      }));
      setItems(Object.fromEntries(data));
    } catch (e) { /* silent — physical_product may have been disabled */ }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({}); setOpenNew(true); };
  const openEdit = (item) => { setEditing({ kind: tab, item }); setForm({ ...item }); setOpenNew(true); };

  const save = async () => {
    const schema = SCHEMAS[tab];
    for (const f of schema) {
      if (f.required && !(form[f.k] || "").toString().trim()) {
        toast.error(`${f.l.replace(" *", "")} is required`); return;
      }
    }
    setSaving(true);
    try {
      // Coerce numeric inputs
      const payload = { ...form };
      for (const f of schema) {
        if (f.type === "number" && payload[f.k] !== "" && payload[f.k] != null) {
          const n = Number(payload[f.k]);
          if (!Number.isNaN(n)) payload[f.k] = n;
        }
      }
      if (editing) {
        await api.patch(`/projects/${projectId}/hw/${tab}/${editing.item.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/projects/${projectId}/hw/${tab}`, payload);
        toast.success("Added");
      }
      setOpenNew(false); setEditing(null); setForm({}); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove this ${KINDS.find((k) => k.id === tab).label} entry?`)) return;
    try {
      await api.delete(`/projects/${projectId}/hw/${tab}/${item.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const schema = SCHEMAS[tab];
  const rows = items[tab] || [];
  const kindMeta = KINDS.find((k) => k.id === tab);

  return (
    <section className="bg-white rounded-sm border border-slate-100" data-testid="hardware-section">
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="label-overline">Hardware</div>
        <div className="font-display text-xl font-bold text-slate-900 mt-1">Physical product workspace</div>
        <p className="text-xs text-slate-500 mt-1">{kindMeta?.hint}</p>
      </div>

      <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-100 overflow-x-auto" data-testid="hw-tabs">
        {KINDS.map((k) => {
          const Icon = k.icon;
          const active = tab === k.id;
          const count = (items[k.id] || []).length;
          return (
            <button
              key={k.id}
              onClick={() => setTab(k.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold whitespace-nowrap transition-colors ${active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid={`hw-tab-${k.id}`}
            >
              <Icon size={12} />
              <span>{k.label}</span>
              <span className={`text-[10px] px-1.5 rounded-full ${active ? "bg-white text-slate-900" : "bg-slate-200 text-slate-700"}`}>{count}</span>
            </button>
          );
        })}
        {canEdit && (
          <div className="ml-auto">
            <Button size="sm" onClick={openCreate} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white h-8" data-testid={`hw-add-${tab}`}>
              <Plus size={12} className="mr-1" /> Add
            </Button>
          </div>
        )}
      </div>

      <div className="p-5">
        {rows.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-10" data-testid={`hw-empty-${tab}`}>
            No entries yet. {canEdit ? "Click Add to create the first one." : ""}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid={`hw-table-${tab}`}>
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  {schema.filter((f) => !f.textarea).map((f) => (
                    <th key={f.k} className="font-semibold py-2 pr-3">{f.l.replace(" *", "")}</th>
                  ))}
                  {canEdit && <th className="w-12" />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`hw-row-${tab}-${r.id}`}>
                    {schema.filter((f) => !f.textarea).map((f) => (
                      <td key={f.k} className="py-2 pr-3 text-slate-700">
                        {f.select && r[f.k] ? <Tag tone={STATUS_TONE[r[f.k]] || "slate"}>{r[f.k]}</Tag> : formatVal(f.k, r[f.k])}
                      </td>
                    ))}
                    {canEdit && (
                      <td className="py-2 text-right whitespace-nowrap">
                        <button onClick={() => openEdit(r)} className="text-slate-400 hover:text-slate-700 p-1" title="Edit" data-testid={`hw-edit-${r.id}`}><Pencil size={11} /></button>
                        <button onClick={() => remove(r)} className="text-slate-400 hover:text-red-600 p-1" title="Remove" data-testid={`hw-remove-${r.id}`}><Trash2 size={11} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={openNew} onOpenChange={(o) => { setOpenNew(o); if (!o) { setEditing(null); setForm({}); } }}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="hw-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit" : "Add"} · {kindMeta?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            {schema.map((f) => (
              <div key={f.k} className={f.full ? "sm:col-span-2" : ""}>
                <Label className="text-[11px] font-semibold text-slate-700">{f.l}</Label>
                {f.select ? (
                  <Select value={form[f.k] || ""} onValueChange={(v) => setForm({ ...form, [f.k]: v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid={`hw-field-${f.k}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {f.select.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.textarea ? (
                  <Textarea rows={3} className="rounded-sm mt-1 resize-none"
                    value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    data-testid={`hw-field-${f.k}`} />
                ) : (
                  <Input className="rounded-sm mt-1" type={f.type || "text"}
                    value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    data-testid={`hw-field-${f.k}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="hw-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
